import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import React from 'react';
import { colors } from '../constants/colors';
import { useMobileDetection } from '../hooks/useMobileDetection';

function HomePage() {
  const [gameCode, setGameCode] = useState('');
  const navigate = useNavigate();
  const { isMobile } = useMobileDetection();

  const handleJoinGame = () => {
    if (gameCode.trim()) {
      navigate(`/join/${gameCode.trim()}`);
    } else {
      alert('Please enter a game code');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleJoinGame();
    }
  };

  const containerStyle = {
    height: '100dvh',
    width: '100%', // Changed from 100vw to 100%
    backgroundColor: colors.outerBackground,
    color: colors.textOnLight,
    paddingTop: isMobile ? 'max(10px, env(safe-area-inset-top))' : '20px',
    paddingBottom: isMobile ? 'max(10px, env(safe-area-inset-bottom))' : '20px',
    fontFamily: 'Arial, sans-serif',
    display: 'grid',
    placeItems: 'center',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    boxSizing: 'border-box',
    margin: '0',
    // Add horizontal padding to ensure content doesn't touch edges
    paddingLeft: isMobile ? '10px' : '20px',
    paddingRight: isMobile ? '10px' : '20px'
  };

  const contentWrapperStyle = {
    width: '100%',
    maxWidth: isMobile ? 'calc(100vw - 20px)' : '800px', // Ensure it fits in viewport
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    boxSizing: 'border-box'
  };

  const homePageStyle = {
    background: colors.innerBackground,
    borderRadius: '20px',
    padding: isMobile ? '15px' : '40px',
    textAlign: 'center',
    color: colors.textOnDark,
    boxShadow: `0 8px 32px rgba(26, 33, 38, 0.3)`,
    width: '100%',
    maxWidth: '100%', // Ensure card doesn't exceed container
    boxSizing: 'border-box'
  };

  const titleStyle = {
    fontSize: isMobile ? '2rem' : '3rem', // Smaller on mobile
    marginBottom: isMobile ? '5px' : '10px', // Much less margin
    marginTop: isMobile ? '10px' : '0', // Small top margin
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
    color: colors.textOnDark
  };

  const subtitleStyle = {
    fontSize: isMobile ? '0.9rem' : '1.2rem', // Smaller
    marginBottom: isMobile ? '20px' : '40px', // Less margin
    color: colors.textOnDark,
    opacity: 0.9
  };

  const gameOptionsStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: isMobile ? '15px' : '30px', // Reduced gap
    marginBottom: isMobile ? '15px' : '30px' // Less margin
  };

  const optionCardStyle = {
    background: colors.cardBackground,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: '15px',
    padding: isMobile ? '15px' : '30px', // Less padding
    backdropFilter: 'blur(5px)'
  };

  const cardTitleStyle = {
    marginBottom: isMobile ? '8px' : '10px', // Less margin
    marginTop: '0',
    fontSize: isMobile ? '1.2rem' : '1.5rem', // Smaller
    color: colors.textOnDark
  };

  const cardTextStyle = {
    marginBottom: isMobile ? '15px' : '20px', // Less margin
    color: colors.textOnDark,
    opacity: 0.8,
    fontSize: isMobile ? '0.85rem' : '1rem' // Smaller
  };

  const buttonPrimaryStyle = {
    backgroundColor: colors.accent,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: isMobile ? '10px 20px' : '12px 24px', // Smaller padding
    fontSize: isMobile ? '14px' : '16px', // Smaller font
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    minWidth: '120px',
    width: isMobile ? '100%' : 'auto'
  };

  const buttonSecondaryStyle = {
    backgroundColor: colors.innerBackground,
    color: colors.textOnDark,
    border: `2px solid ${colors.accent}`,
    borderRadius: '8px',
    padding: isMobile ? '10px 20px' : '12px 24px', // Smaller padding
    fontSize: isMobile ? '14px' : '16px', // Smaller font
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    minWidth: '120px',
    width: isMobile ? '100%' : 'auto'
  };

  const joinSectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? '10px' : '15px', // Less gap
    alignItems: 'center'
  };

  const gameCodeInputStyle = {
    padding: isMobile ? '10px' : '12px', // Smaller padding
    border: `2px solid ${colors.accent}`,
    borderRadius: '8px',
    fontSize: isMobile ? '14px' : '16px', // Smaller font
    textAlign: 'center',
    textTransform: 'uppercase',
    width: '100%',
    maxWidth: isMobile ? '100%' : '200px',
    background: colors.inputBackground,
    color: colors.textOnLight,
    WebkitAppearance: 'none' // Prevent iOS styling
  };

  const footerStyle = {
    marginTop: isMobile ? '15px' : '30px', // Less margin
    marginBottom: '0',
    opacity: 0.7,
    fontSize: isMobile ? '0.8rem' : '0.9rem', // Smaller
    color: colors.textOnDark
  };

  return (
    <div style={containerStyle}>
      <div style={contentWrapperStyle}>
        <div style={homePageStyle}>
          <h1 style={titleStyle}>🎯 Trivia Quest</h1>
          <p style={subtitleStyle}>Test your knowledge with friends!</p>
          
          <div style={gameOptionsStyle}>
            <div style={optionCardStyle}>
              <h3 style={cardTitleStyle}>Create a Game</h3>
              <p style={cardTextStyle}>Start a new trivia session</p>
              <Link to="/create" style={{textDecoration: 'none'}}>
                <button 
                  style={buttonPrimaryStyle}
                  onMouseEnter={(e) => !isMobile && (e.target.style.backgroundColor = colors.accentHover)}
                  onMouseLeave={(e) => !isMobile && (e.target.style.backgroundColor = colors.accent)}
                >
                  Create Game
                </button>
              </Link>
            </div>
            
            <div style={optionCardStyle}>
              <h3 style={cardTitleStyle}>Join a Game</h3>
              <p style={cardTextStyle}>Enter your game code</p>
              <div style={joinSectionStyle}>
                <input 
                  type="text"
                  placeholder="ENTER GAME CODE"
                  value={gameCode}
                  onChange={(e) => setGameCode(e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={gameCodeInputStyle}
                  onFocus={(e) => !isMobile && (e.target.style.boxShadow = `0 0 0 2px ${colors.accentBorder}`)}
                  onBlur={(e) => !isMobile && (e.target.style.boxShadow = 'none')}
                />
                <button 
                  style={buttonSecondaryStyle}
                  onClick={handleJoinGame}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = colors.accent;
                      e.target.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = colors.innerBackground;
                      e.target.style.color = colors.textOnDark;
                    }
                  }}
                >
                  Join Game
                </button>
              </div>
            </div>
          </div>

          <div style={footerStyle}>
            Ready to test your trivia skills?
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;